import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Modal,
  Button,
  Form,
  Table,
  Alert,
  Spinner,
  Card,
} from 'react-bootstrap';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaListUl } from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const InstallmentsManagement = () => {
  const { feeSlabId } = useParams();
  const navigate = useNavigate();
  const [installments, setInstallments] = useState([]);
  const [feeSlab, setFeeSlab] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    installment_no: '',
    title: '',
    amount: '',
    due_after_days: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

  useEffect(() => {
    fetchFeeSlab();
    fetchInstallments();
  }, [feeSlabId]);

  const fetchFeeSlab = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}${appConstants.FEE_SLAB.GET}?id=${feeSlabId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        setFeeSlab(response.data.data);
      } else {
        showAlert('Failed to fetch fee slab details', 'danger');
      }
    } catch (error) {
      console.error('Error fetching fee slab:', error);
      const errorMessage =
        error.response?.data?.message || 'Error loading fee slab';
      showAlert(errorMessage, 'danger');
    }
  };

  const fetchInstallments = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}${appConstants.INSTALLMENTS.LIST}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { slab_id: feeSlabId },
        },
      );

      if (response.data.status === 'success') {
        setInstallments(response.data.data || []);
      } else {
        showAlert('Failed to fetch installments', 'danger');
      }
    } catch (error) {
      console.error('Error fetching installments:', error);
      const errorMessage =
        error.response?.data?.message || 'Error loading installments';
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    if (
      !formData.installment_no ||
      !formData.title ||
      !formData.amount ||
      formData.due_after_days === ''
    ) {
      showAlert('Please fill in all required fields', 'warning');
      return;
    }

    setSaving(true);
    try {
      const endpoint = formData.id
        ? appConstants.INSTALLMENTS.UPDATE
        : appConstants.INSTALLMENTS.CREATE;

      const payload = {
        slab_id: parseInt(feeSlabId),
        installment_no: parseInt(formData.installment_no),
        title: formData.title,
        amount: parseFloat(formData.amount),
        due_after_days: parseInt(formData.due_after_days),
      };

      if (formData.id) {
        payload.id = formData.id;
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showAlert(
          `Installment ${formData.id ? 'updated' : 'created'} successfully!`,
          'success',
        );
        setShowModal(false);
        resetForm();
        fetchInstallments();
      } else {
        showAlert(
          response.data.message || 'Failed to save installment',
          'danger',
        );
      }
    } catch (error) {
      console.error('Error saving installment:', error);
      const errorMessage =
        error.response?.data?.message || 'Error saving installment';
      showAlert(errorMessage, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (installment) => {
    setFormData({
      id: installment.id,
      installment_no: installment.installment_no || '',
      title: installment.title || '',
      amount: installment.amount || '',
      due_after_days:
        installment.due_after_days !== undefined &&
        installment.due_after_days !== null
          ? installment.due_after_days.toString()
          : '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this installment?')) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${appConstants.INSTALLMENTS.DELETE}`,
        { id: id },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showAlert('Installment deleted successfully!', 'success');
        fetchInstallments();
      } else {
        showAlert(
          response.data.message || 'Failed to delete installment',
          'danger',
        );
      }
    } catch (error) {
      console.error('Error deleting installment:', error);
      const errorMessage =
        error.response?.data?.message || 'Error deleting installment';
      showAlert(errorMessage, 'danger');
    }
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      id: null,
      installment_no: '',
      title: '',
      amount: '',
      due_after_days: '',
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const getTotalAmount = () => {
    return installments.reduce(
      (total, installment) => total + parseFloat(installment.amount || 0),
      0,
    );
  };

  // Remove getClassName function since it's no longer needed for fee slabs

  return (
    <Layout>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="me-3"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft className="me-2" />
              Back to Fee Slabs
            </Button>
            <h4 className="d-inline-block mb-0">
              <FaListUl className="me-2" />
              Installments Management
            </h4>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowModal(true)}
            style={{ backgroundColor: '#0657CE' }}
          >
            <FaPlus className="me-2" />
            Add Installment
          </Button>
        </div>

        {feeSlab && (
          <Card className="mb-3">
            <Card.Body>
              <div className="row">
                <div className="col-md-3">
                  <strong>Period:</strong> {feeSlab.from_date} to{' '}
                  {feeSlab.to_date}
                </div>
                <div className="col-md-3">
                  <strong>Tuition Discount:</strong> ₹
                  {parseFloat(feeSlab.tuition_discount || 0).toLocaleString()}
                </div>
                <div className="col-md-3">
                  <strong>Effective Tuition:</strong> ₹
                  {parseFloat(
                    feeSlab.effective_tuition_fee || 0,
                  ).toLocaleString()}
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Floating Alert (bottom left) */}
        {alert.show && (
          <div
            style={{
              position: 'fixed',
              left: '18px',
              bottom: '18px',
              zIndex: 9999,
              minWidth: '180px',
              maxWidth: '320px',
              pointerEvents: 'auto',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}
          >
            <div
              style={{
                position: 'relative',
                borderRadius: '6px',
                background:
                  alert.variant === 'success'
                    ? '#e6f7e6'
                    : alert.variant === 'danger'
                      ? '#ffeaea'
                      : '#fff8e1',
                color: '#5c4200',
                marginBottom: 0,
                fontWeight: 500,
                fontSize: '0.92rem',
                padding: '6px 32px 6px 12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                maxWidth: '320px',
              }}
            >
              <span style={{ flex: 1 }}>{alert.message}</span>
              <button
                onClick={() =>
                  setAlert({ show: false, message: '', variant: '' })
                }
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '8px',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#5c4200',
                  fontWeight: 700,
                  fontSize: '1.1em',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Installments Table */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <Table striped bordered hover responsive>
              <thead style={{ backgroundColor: '#0657CE', color: 'white' }}>
                <tr>
                  <th>Installment No.</th>
                  <th>Title</th>
                  <th>Amount</th>
                  <th>Due After Days</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {installments.length > 0 ? (
                  installments.map((installment) => (
                    <tr key={installment.id}>
                      <td>{installment.installment_no}</td>
                      <td>{installment.title}</td>
                      <td>
                        ₹{parseFloat(installment.amount || 0).toLocaleString()}
                      </td>
                      <td>{installment.due_after_days} days</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEdit(installment)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(installment.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No installments found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {installments.length > 0 && (
              <Card className="mt-3">
                <Card.Body>
                  <div className="row text-center">
                    <div className="col-md-4">
                      <h6>Total Installments</h6>
                      <h5>{installments.length}</h5>
                    </div>
                    <div className="col-md-4">
                      <h6>Total Amount (Installments)</h6>
                      <h5>₹{getTotalAmount().toLocaleString()}</h5>
                    </div>
                    {feeSlab && (
                      <div className="col-md-4">
                        <h6>Remaining Amount</h6>
                        <h5
                          className={
                            parseFloat(feeSlab.effective_tuition_fee) -
                              getTotalAmount() ===
                            0
                              ? 'text-success'
                              : 'text-warning'
                          }
                        >
                          ₹
                          {(
                            parseFloat(feeSlab.effective_tuition_fee) -
                            getTotalAmount()
                          ).toLocaleString()}
                        </h5>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {formData.id ? 'Edit Installment' : 'Add New Installment'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Installment Number *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter installment number"
                    value={formData.installment_no}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        installment_no: e.target.value,
                      })
                    }
                    min="1"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Title *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter installment title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Amount *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter installment amount"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Due After Days *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Days after which this installment is due (0 for immediate)"
                    value={formData.due_after_days}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        due_after_days: e.target.value,
                      })
                    }
                    min="0"
                  />
                  <Form.Text className="text-muted">
                    Number of days after enrollment when this installment
                    becomes due (0 = due immediately)
                  </Form.Text>
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#0657CE' }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>{formData.id ? 'Update' : 'Create'}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default InstallmentsManagement;
